import { appAxiosInstance } from '@/config/axios';
import { getStructuredLinkedInProfileData } from '@/models/linkedin';
import { getStructuredFormSubmissionsResponse } from '@/models/submissions';
import {
	getStructuredFormFillSubmitResponse,
	getStructuredFormSubmission,
	getStructuredUploadFileResponse,
	getStructuredUserSubmissions,
} from '@/models/submissions/submission';

import { FORM_SUBMISSIONS_PARALLEL_BATCH_SIZE } from '@/utils/constants';
import { transformKeysSnakeToCamelCase } from '@/utils/transformation';

import {
	IAddSubmissionCommentsPayload,
	ICheckDuplicateCandidatePayload,
	ICommentAttachmentDraftFileMetadata,
	IDeleteSubmissionCommentPayload,
	IEditSubmissionCommentPayload,
	IGetCommentFilePayload,
	IEditSubmissionPayload,
	IFetchSubmissionCommentsPayload,
	IFetchVideoTranscriptPayload,
	ISFetchVideoTranscriptResponse,
	IFormFillSubmitPayload,
	IFormFillSubmitResponse,
	IGetFormSubmissionPayload,
	IGetFormSubmissionsPayload,
	IGetFormSubmissionsResponse,
	IGetLinkedInProfilePayload,
	IGetLinkedInProfileResponse,
	IGetSubmissionFilePayload,
	IGetSubmissionPDFPayload,
	IGetUserSubmissionsPayload,
	IGetUserSubmissionsResponse,
	IMapSubmissionToEmailPayload,
	IResumeAutofillPayload,
	ISCheckDuplicateCandidateResponse,
	ISGetFormSubmissionsResponse,
	ISResumeAutofillResponse,
	ISSubmissionActivityResponse,
	ISubmissionActivityPayload,
	ISEditSubmissionResponse,
	ISFetchSubmissionCommentsResponse,
	ISVideoDownloadResponse,
	ISaveTableFiltersPayload,
	IUpdateSubmissionCommentsPayload,
	IUploadCommentFilePayload,
	IUploadFilePayload,
	IUploadFileResponse,
	IVideoDownloadPayload,
	IupdateSubmissionStagePayload,
} from './submissions.types';

export async function getFormSubmissions(
	payload: IGetFormSubmissionsPayload,
	signal?: AbortSignal
) {
	const res = await appAxiosInstance.post('/submission/fetch-for-form', payload, { signal });

	const data = res.data as IGetFormSubmissionsResponse;

	return getStructuredFormSubmissionsResponse(data, payload.page_details);
}

/** First page alone (it carries total_count), then the rest in parallel, merged into one response. */
export async function getAllFormSubmissionPages(
	payload: IGetFormSubmissionsPayload,
	onFirstPage?: (firstPage: ISGetFormSubmissionsResponse) => void
): Promise<ISGetFormSubmissionsResponse> {
	const firstPage = await getFormSubmissions(payload);
	onFirstPage?.(firstPage);

	const { page_size: pageSize } = payload.page_details;
	const totalPages = Math.ceil(firstPage.pageDetails.totalCount / pageSize);
	const restPayloads = Array.from(
		{ length: Math.max(totalPages - 1, 0) },
		(_, i): IGetFormSubmissionsPayload => ({
			...payload,
			skip_total_count: true,
			page_details: { ...payload.page_details, offset: (i + 1) * pageSize },
		})
	);

	// Batched so large forms don't fire every page request at once
	const restPages: ISGetFormSubmissionsResponse[] = [];
	for (let i = 0; i < restPayloads.length; i += FORM_SUBMISSIONS_PARALLEL_BATCH_SIZE) {
		const batch = restPayloads.slice(i, i + FORM_SUBMISSIONS_PARALLEL_BATCH_SIZE);
		restPages.push(...(await Promise.all(batch.map((page) => getFormSubmissions(page)))));
	}

	return {
		...firstPage,
		submissions: [firstPage, ...restPages].flatMap((page) => page.submissions),
	};
}

/** Every page of a search, sequentially while `has_next`. Stage counts come with the first page. */
export async function searchFormSubmissions(
	payload: IGetFormSubmissionsPayload,
	signal?: AbortSignal
): Promise<ISGetFormSubmissionsResponse> {
	const firstPage = await getFormSubmissions(
		{ ...payload, fetch_status_counts: true, page_details: { ...payload.page_details, offset: 0 } },
		signal
	);

	const submissions = [...firstPage.submissions];
	let { pageDetails } = firstPage;
	while (pageDetails.hasNext) {
		const page = await getFormSubmissions(
			{
				...payload,
				page_details: { ...payload.page_details, offset: pageDetails.offset + pageDetails.pageSize },
			},
			signal
		);
		submissions.push(...page.submissions);
		pageDetails = page.pageDetails;
	}

	return { ...firstPage, submissions };
}

export async function getFormSubmission(payload: IGetFormSubmissionPayload) {
	const res = await appAxiosInstance.post('/submission/fetch-submission-for-form', payload);

	return getStructuredFormSubmission(res.data);
}

export async function updateSubmissionStage(payload: IupdateSubmissionStagePayload) {
	const res = await appAxiosInstance.post('/submission/update-status', payload);

	return res.data as string;
}

export async function editSubmission(payload: IEditSubmissionPayload) {
	const res = await appAxiosInstance.post('/submission/edit-submission-for-admin', payload);

	return transformKeysSnakeToCamelCase(res.data) as unknown as ISEditSubmissionResponse;
}

export async function updateSubmissionComments(payload: IUpdateSubmissionCommentsPayload) {
	const res = await appAxiosInstance.post('/submission/update-comments', payload);

	return res.data as string;
}

export async function getLinkedInProfile(payload: IGetLinkedInProfilePayload) {
	const res = await appAxiosInstance.post('/submission/fetch-linkedin', payload);
	const data = res.data as IGetLinkedInProfileResponse;

	if (!data?.profile_data) {
		return null;
	}

	return getStructuredLinkedInProfileData(data.profile_data);
}

export async function fetchVideoTranscript(payload: IFetchVideoTranscriptPayload) {
	const res = await appAxiosInstance.post('/submission/fetch-video-transcript', payload);
	return transformKeysSnakeToCamelCase(res.data) as unknown as ISFetchVideoTranscriptResponse;
}

export async function getSubmissionPDF(payload: IGetSubmissionPDFPayload) {
	const res = await appAxiosInstance.post('/submission/pdf-download', payload, {
		responseType: 'blob',
	});
	return res.data;
}

export async function getSubmissionFile(payload: IGetSubmissionFilePayload) {
	const res = await appAxiosInstance.post('/submission/file-download', payload, {
		responseType: 'blob',
	});

	// Return both blob and headers for metadata extraction
	return {
		blob: res.data,
		headers: res.headers,
	};
}

export async function getUserSubmissions(payload: IGetUserSubmissionsPayload) {
	const res = await appAxiosInstance.post('submission/fetch-for-user', payload);

	const data = res.data as IGetUserSubmissionsResponse;

	return getStructuredUserSubmissions(data, payload.page_details);
}

export async function uploadFile(payload: IUploadFilePayload) {
	const formData = new FormData();

	formData.append('formIdentifier', payload.formId);
	formData.append('fieldId', payload.fieldId);
	formData.append('file', payload.file);
	if (payload.isReferral) formData.append('isReferral', payload.isReferral.toString());

	const res = await appAxiosInstance.post('/submission/upload-file', formData, {
		headers: {
			'Content-Type': 'multipart/form-data',
		},
	});

	const data = res.data as IUploadFileResponse;

	return getStructuredUploadFileResponse(data);
}

export async function formFillSubmit(payload: IFormFillSubmitPayload) {
	let timeZone: string | undefined;
	try {
		timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
	} catch {
		timeZone = undefined;
	}

	const payloadWithTimeZone = timeZone ? { ...payload, time_zone: timeZone } : payload;

	const res = await appAxiosInstance.post('/submission/submit-for-user', payloadWithTimeZone);

	const data = res.data as IFormFillSubmitResponse;

	return getStructuredFormFillSubmitResponse(data);
}

export async function formFillSubmitWithoutEmailVerification(payload: IFormFillSubmitPayload) {
	let timeZone: string | undefined;
	try {
		timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
	} catch {
		timeZone = undefined;
	}

	const payloadWithTimeZone = timeZone ? { ...payload, time_zone: timeZone } : payload;

	const res = await appAxiosInstance.post('/submission/submit', payloadWithTimeZone);

	const data = res.data as IFormFillSubmitResponse;

	return getStructuredFormFillSubmitResponse(data);
}

export async function fetchSubmissionComments(payload: IFetchSubmissionCommentsPayload) {
	const res = await appAxiosInstance.post('/submission/fetch-comments', payload);

	return transformKeysSnakeToCamelCase(res.data) as unknown as ISFetchSubmissionCommentsResponse;
}

export async function addSubmissionComments(payload: IAddSubmissionCommentsPayload) {
	const res = await appAxiosInstance.post('/submission/add-comment', payload);
	return res.data as string;
}

export async function editSubmissionComment(payload: IEditSubmissionCommentPayload) {
	const res = await appAxiosInstance.post('/submission/edit-comment', payload);
	return res.data as string;
}

export async function uploadCommentFile(payload: IUploadCommentFilePayload) {
	const formData = new FormData();
	formData.append('formIdentifier', payload.formIdentifier);
	formData.append('submissionId', payload.submissionId);
	formData.append('commentId', payload.commentId);
	formData.append('file', payload.file);

	const res = await appAxiosInstance.post('/submission/upload-comment-file', formData, {
		headers: { 'Content-Type': 'multipart/form-data' },
	});

	return res.data?.draft_file_metadata as ICommentAttachmentDraftFileMetadata;
}

export async function getCommentFile(payload: IGetCommentFilePayload) {
	const res = await appAxiosInstance.post('/submission/comment-file-download', payload, {
		responseType: 'blob',
	});

	return {
		blob: res.data,
		headers: res.headers,
	};
}

export async function deleteSubmissionComment(payload: IDeleteSubmissionCommentPayload) {
	const res = await appAxiosInstance.post('/submission/delete-comment', payload);
	return res.data as string;
}

export async function downloadVideo(payload: IVideoDownloadPayload) {
	try {
		const res = await appAxiosInstance.post('/submission/video-download', payload, {
			responseType: 'blob', // Important: Expect blob response like other file downloads
		});

		// Check if response is successful
		if (res.status !== 200) {
			throw new Error(`API returned status ${res.status}`);
		}

		// Check if response is a blob (file)
		if (!(res.data instanceof Blob)) {
			throw new Error('API did not return a file blob');
		}

		return {
			downloadUrl: URL.createObjectURL(res.data), // Create object URL from blob
		} as ISVideoDownloadResponse;
	} catch (error) {
		console.error('Download video API error:', error);
		throw error;
	}
}

export async function saveTableFilters(payload: ISaveTableFiltersPayload) {
	const res = await appAxiosInstance.post('/form/save-filter', payload);
	return transformKeysSnakeToCamelCase(res.data) as unknown as string;
}

export async function mapSubmissionToEmail(payload: IMapSubmissionToEmailPayload) {
	const res = await appAxiosInstance.post('/submission/map-to-email', payload);
	return res.data as string;
}

export async function checkDuplicateCandidate(payload: ICheckDuplicateCandidatePayload) {
	const res = await appAxiosInstance.post('/submission/check-duplicate-in-workspace', payload);
	return transformKeysSnakeToCamelCase(res.data) as unknown as ISCheckDuplicateCandidateResponse;
}

export async function autofillFromResume(payload: IResumeAutofillPayload) {
	const res = await appAxiosInstance.post('/submission/autofill-from-resume', payload);
	return transformKeysSnakeToCamelCase(res.data) as unknown as ISResumeAutofillResponse;
}

export async function fetchSubmissionActivity(payload: ISubmissionActivityPayload) {
	const res = await appAxiosInstance.post('/submission/activity', payload);
	return transformKeysSnakeToCamelCase(res.data) as unknown as ISSubmissionActivityResponse;
}
